import React, { useState, useMemo } from "react";
import UEHeroCard from "./ue-hero-card";
//import { BadgeType } from "@/app/enums/badges";
import { useQuery } from "@tanstack/react-query";
import { getUEListData } from "@/app/api/service/homepage";
import { UEListData } from "@/app/interface/homepage";
import { ListingCategory } from "@/app/enums/unlisted";
// import useMobile from "@/app/hooks/use-mobile";
import Link from "next/link";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import { getUnlistedShareProductPath } from "@/app/unlisted-shares/[...id]/slug";
import { isVisibleOnBrowse } from "@/app/utils/product-visibility";
import { ICM_NAME_FULL } from "@/app/utils/constants";

interface UeProps {
    redirectTo: string;
    initialData?: { data: UEListData[]; success: boolean } | null;
}
const UEHero: React.FC<UeProps> = ({ redirectTo, initialData }) => {
    const [showAll, setShowAll] = useState(false);

    const { data: ueData, isLoading } = useQuery({
        queryKey: ["ueListData"],
        queryFn: () => getUEListData(),
        initialData: initialData ?? undefined, // Use SSR data for initial render (SEO)
    });

    // react memo
    const VISIBLE_PRODUCTS_COUNT = 10;

    const filteredData = useMemo(() => {
        if (!ueData?.success) return [];

        return ueData.data.filter(
            (item: UEListData) =>
                item?.product &&
                !item?.ISIN?.includes("BASKET") &&
                item?.company?.logoUrl?.length > 20 &&
                item?.category === ListingCategory.LIVE &&
                isVisibleOnBrowse(item),
        );
    }, [ueData]);

    const visibleData = useMemo(() => {
        return showAll ? filteredData : filteredData.slice(0, VISIBLE_PRODUCTS_COUNT);
    }, [showAll, filteredData]);

    const handleToggleView = () => {
        setShowAll((prev) => {
            const next = !prev;

            setAnalyticsEvent(
                redirectTo,
                next ? "US_Listing_Page_View_All_Fold3" : "US_Listing_Page_Show_Less_Fold3",
            );

            return next;
        });
    };
    // const { isMobile } = useMobile();

    return (
        <div className="py-s11 flex flex-col items-center justify-center align-middle md:py-s18">
            <h2 className="mb-3 text-center text-5xl font-semibold leading-[130%] tracking-[-0.4px] md:mb-5 md:text-6xl md:leading-[130%] md:tracking-[-1%]">
                Live Opportunities on {ICM_NAME_FULL}
            </h2>
            {/* <div className="text-center">
                <p className="text-base md:text-3xl md:leading-[39.84px]">
                    Grab your chance to invest in India’s top companies. {!isMobile && <br />}
                    <span className="font-bold md:text-2xl">Get started with just 1 share.</span>
                </p>
            </div> */}

            <div className="mt-s10 grid grid-cols-1 gap-[8px] md:my-s10 md:grid-cols-2 md:gap-4">
                {isLoading
                    ? Array.from({ length: 8 }).map((_, index) => (
                          <div
                              key={index}
                              className="bg-red w-[300px] animate-pulse rounded-2xl border-2 border-gray-200 p-4 md:w-[442px] md:p-6"
                          >
                              <div className="flex gap-4 bg-gray-200">
                                  {/* Image container with fixed dimensions */}
                                  <div className="h-12 w-12 flex-shrink-0 md:h-[84px] md:w-[84px]"></div>

                                  {/* Content wrapper with proper flex behavior */}
                                  <div className="flex min-w-0 flex-1 items-center justify-between">
                                      {/* Text content with proper truncation */}
                                      <div className="flex min-w-0 max-w-[calc(100%-40px)] flex-col gap-2">
                                          <div className="overflow-hidden">
                                              <h3 className="truncate text-sm font-semibold md:text-lg"></h3>
                                          </div>
                                          <div className="whitespace-nowrap text-sm">
                                              <span className="text-gray-400"></span>
                                          </div>
                                      </div>

                                      {/* Button with fixed width */}
                                      <div className="flex-shrink-0"></div>
                                  </div>
                              </div>
                          </div>
                      ))
                    : visibleData.map((card: UEListData, index: number) => (
                          <UEHeroCard key={index} {...card} />
                      ))}
            </div>

            {/* Hidden SEO links for all products - visible to crawlers but not to users */}
            {filteredData && filteredData.length > 0 && (
                <div className="sr-only" aria-hidden="true">
                    {filteredData
                        .filter((card: UEListData) => card?.config?.allowSeoIndexing === true)
                        .map((card: UEListData, index: number) => {
                            return (
                                <Link
                                    key={`seo-${index}`}
                                    href={getUnlistedShareProductPath(
                                        card?.product ?? "",
                                        card?.company?.displayName,
                                    )}
                                >
                                    {card?.company?.displayName}
                                </Link>
                            );
                        })}
                </div>
            )}
            {/* <Link href={redirectTo}> */}
            <RegularButton
                noIcon
                showRightIcon={false}
                size={ButtonSize.Large}
                variant={ButtonVariant.SecondaryDefault}
                className="mt-5 w-full max-w-full md:mt-0 md:w-auto"
                // onClick={() => {
                //     setAnalyticsEvent(redirectTo, "US_Listing_Page_Start_Investing_Fold3"); // Track the event
                // }}
                label={showAll ? "Show less" : "View all Unlisted Shares"}
                onClick={handleToggleView}
            />
            {/* </Link> */}

            {/* <div className="text-center">
                <h3 className="text-base font-medium text-neutral-300 md:text-base">IN THE NEWS</h3>
                <EmblaCarousel
                    data={feedbacks.map((feedback, index) => (
                        <div key={index}>
                            <p className="text-xl font-semibold md:text-3xl">
                                {feedback.text}
                            </p>
                            <p className="text-base font-medium text-neutral-300 md:text-lg">
                                {feedback.name}
                            </p>
                        </div>
                    ))}
                    showDots={true}
                    showPrevOrNext={false}
                    autoPlay={true}
                />
            </div> */}
        </div>
    );
};

export default UEHero;
